import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import {
  TICKET_TIPO_LABEL, TICKET_PRIORITA_LABEL, TICKET_STATO_LABEL, ticketStatoTesto,
  ticketStatoBadge, ticketPrioritaBadge,
  TicketPriorita, TicketStato, TicketDettaglio,
} from './ticketAssistenza';
import { API_DOCS } from './SchedaTicketAssistenza.api';
import './Assistenza.css';

const MODULO = 'assistenza.richieste';

function formatDimensione(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SchedaTicketAssistenza() {
  const { id } = useParams<{ id: string }>();
  const { can, gestisceAssistenza, utente } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const [ticket, setTicket] = useState<TicketDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [nuovaRisposta, setNuovaRisposta] = useState('');
  const [inviando, setInviando] = useState(false);
  const [savingStato, setSavingStato] = useState(false);
  const [chatAperta, setChatAperta] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [riaperturaAperta, setRiaperturaAperta] = useState(false);
  const [motivoRiapertura, setMotivoRiapertura] = useState('');
  const [confermando, setConfermando] = useState(false);
  const navigate = useNavigate();

  // Solo SUPERADMIN: cancella il ticket con allegati, risposte e log
  const eliminaTicket = async () => {
    if (!ticket) return;
    if (!window.confirm(`Eliminare definitivamente il ticket #${ticket.id} "${ticket.titolo}"?\n\nVengono cancellati anche allegati, risposte e log. L'operazione non è reversibile.`)) return;
    setEliminando(true);
    setError('');
    try {
      // @api DELETE /api/assistenza/ticket/:id -> { success: true }
      await apiFetch(`/api/assistenza/ticket/${ticket.id}`, { method: 'DELETE' });
      navigate('/assistenza/richieste');
    } catch (e: any) {
      setError(e.message || 'Errore nella cancellazione del ticket');
      setEliminando(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // @api GET /api/assistenza/ticket/:id -> { success: true, data: { id, tipo, priorita, stato, titolo, descrizione, modulo, urlPagina, createdById, createdByName, createdAt, updatedAt, chiusoAt, societa: { id, nome }, allegati: [{ id, nomeFile, mimeType, dimensione, uploadedAt }], risposte: [{ id, utenteId, utenteName, testo, isStaff, createdAt }], chat: { id, stato, documentiKb, messaggi: [{ id, ruolo, testo, allegati: [{ nomeFile, mimeType, dimensione }] | null, createdAt }] } | null } }
      const res = await apiFetch<{ success: boolean; data: TicketDettaglio }>(`/api/assistenza/ticket/${id}`);
      setTicket(res.data);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const aggiornaCampo = async (data: { stato?: TicketStato; priorita?: TicketPriorita }) => {
    if (!ticket) return;
    setSavingStato(true);
    try {
      // @api PUT /api/assistenza/ticket/:id { stato?, priorita? } -> { success: true }
      await apiFetch(`/api/assistenza/ticket/${ticket.id}`, { method: 'PUT', body: JSON.stringify(data) });
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nell\'aggiornamento');
    } finally {
      setSavingStato(false);
    }
  };

  const inviaRisposta = async () => {
    if (!ticket || !nuovaRisposta.trim()) return;
    setInviando(true);
    try {
      // @api POST /api/assistenza/ticket/:id/risposte { testo } -> { success: true, data: { id } }
      await apiFetch(`/api/assistenza/ticket/${ticket.id}/risposte`, {
        method: 'POST',
        body: JSON.stringify({ testo: nuovaRisposta.trim() }),
      });
      setNuovaRisposta('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nell\'invio della risposta');
    } finally {
      setInviando(false);
    }
  };

  // Chi ha aperto il ticket conferma che il problema è risolto: la richiesta si chiude
  const confermaChiusura = async () => {
    if (!ticket) return;
    setConfermando(true);
    setError('');
    try {
      // @api POST /api/assistenza/ticket/:id/conferma-chiusura -> { success: true }
      await apiFetch(`/api/assistenza/ticket/${ticket.id}/conferma-chiusura`, { method: 'POST' });
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nella conferma di chiusura');
    } finally {
      setConfermando(false);
    }
  };

  // …oppure la riapre: il motivo è obbligatorio e finisce nel thread
  const riapriRichiesta = async () => {
    if (!ticket || !motivoRiapertura.trim()) return;
    setConfermando(true);
    setError('');
    try {
      // @api POST /api/assistenza/ticket/:id/riapri { motivo } -> { success: true }
      await apiFetch(`/api/assistenza/ticket/${ticket.id}/riapri`, {
        method: 'POST',
        body: JSON.stringify({ motivo: motivoRiapertura.trim() }),
      });
      setMotivoRiapertura('');
      setRiaperturaAperta(false);
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nella riapertura della richiesta');
    } finally {
      setConfermando(false);
    }
  };

  const scaricaAllegato = async (allegatoId: number, nomeFile: string) => {
    if (!ticket) return;
    try {
      // @api GET /api/assistenza/ticket/:id/allegati/:allegatoId/download -> (file stream)
      await apiDownload(`/api/assistenza/ticket/${ticket.id}/allegati/${allegatoId}/download`, nomeFile);
    } catch (e: any) {
      setError(e.message || 'Errore nel download');
    }
  };

  if (loading) {
    return <Layout><div className="assistenza-scheda"><div className="bop-empty-state">Caricamento…</div></div></Layout>;
  }

  if (error && !ticket) {
    return (
      <Layout>
        <div className="assistenza-scheda">
          <div className="bop-inline-error">{error}</div>
          <BopBackLink to="/assistenza/richieste" label="Torna alle richieste" />
        </div>
      </Layout>
    );
  }

  if (!ticket) return null;

  return (
    <Layout>
      <div className="assistenza-scheda">
        <BopBackLink to="/assistenza/richieste" label="Torna alle richieste" />
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>#{ticket.id} — {ticket.titolo}</h2>
            <p>
              Aperto da {ticket.createdByName} il {new Date(ticket.createdAt).toLocaleString('it-IT')}
              {ticket.societa ? ` · ${ticket.societa.nome}` : ''}
            </p>
          </div>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        {/* Richiesta risolta dallo staff: chi l'ha aperta conferma o riapre */}
        {ticket.stato === 'RISOLTO' && utente?.id === ticket.createdById && (
          <div className="bop-card">
            <div className="bop-card__header"><h3>Lo staff ha segnato la richiesta come risolta</h3></div>
            <div className="bop-card__body">
              <p className="bop-conferma-chiusura__testo">
                Se il problema è risolto conferma la chiusura. Se non lo è, riapri la richiesta
                spiegando cosa non va: finché non fai una delle due cose ricevi un promemoria al giorno.
              </p>
              {riaperturaAperta ? (
                <>
                  <textarea
                    className="bop-input bop-conferma-chiusura__motivo"
                    rows={3}
                    placeholder="Perché la richiesta non è risolta?"
                    value={motivoRiapertura}
                    onChange={e => setMotivoRiapertura(e.target.value)}
                  />
                  <div className="bop-conferma-chiusura__azioni">
                    <button className="bop-btn bop-btn-primary" onClick={riapriRichiesta} disabled={confermando || !motivoRiapertura.trim()}>
                      {confermando ? 'Riapertura…' : 'Riapri la richiesta'}
                    </button>
                    <button className="bop-btn bop-btn-ghost" onClick={() => { setRiaperturaAperta(false); setMotivoRiapertura(''); }} disabled={confermando}>
                      Annulla
                    </button>
                  </div>
                </>
              ) : (
                <div className="bop-conferma-chiusura__azioni">
                  <button className="bop-btn bop-btn-primary" onClick={confermaChiusura} disabled={confermando}>
                    {confermando ? 'Chiusura…' : 'Conferma chiusura'}
                  </button>
                  <button className="bop-btn bop-btn-secondary" onClick={() => setRiaperturaAperta(true)} disabled={confermando}>
                    Riapri la richiesta
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="assist-scheda-grid">
          {/* Colonna principale */}
          <div className="assist-scheda-main">
            <div className="bop-card">
              <div className="bop-card__header"><h3>Dettaglio richiesta</h3></div>
              <div className="bop-card__body">
                <div className="assist-badges">
                  <span className="bop-badge bop-badge--primary">{TICKET_TIPO_LABEL[ticket.tipo]}</span>
                  <span className={`bop-badge ${ticketPrioritaBadge(ticket.priorita)}`}>{TICKET_PRIORITA_LABEL[ticket.priorita]}</span>
                  <span className={`bop-badge ${ticketStatoBadge(ticket.stato)}`}>{ticketStatoTesto(ticket.stato, gestisceAssistenza)}</span>
                </div>

                <p className="assist-descrizione">{ticket.descrizione}</p>

                <div className="assist-meta-grid">
                  {ticket.modulo && (
                    <div className="assist-meta-item">
                      <span className="bop-label">Modulo</span>
                      <span>{ticket.modulo}</span>
                    </div>
                  )}
                  {ticket.urlPagina && (
                    <div className="assist-meta-item">
                      <span className="bop-label">URL pagina</span>
                      <a href={ticket.urlPagina} target="_blank" rel="noopener noreferrer">{ticket.urlPagina}</a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {ticket.allegati.length > 0 && (
              <div className="bop-card">
                <div className="bop-card__header"><h3>Allegati ({ticket.allegati.length})</h3></div>
                <div className="bop-card__body">
                  <div className="assist-allegati">
                    {ticket.allegati.map(a => (
                      <button key={a.id} className="assist-allegato" onClick={() => scaricaAllegato(a.id, a.nomeFile)}>
                        <span className="assist-allegato__nome">{a.nomeFile}</span>
                        <span className="bop-text-muted assist-allegato__dim">{formatDimensione(a.dimensione)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Chat con l'assistente da cui è nato il ticket */}
            {ticket.chat && ticket.chat.messaggi.length > 0 && (
              <div className="bop-card">
                <div className="bop-card__header">
                  <h3>Conversazione con l'assistente ({ticket.chat.messaggi.length})</h3>
                  <button className="bop-btn bop-btn-ghost" onClick={() => setChatAperta(a => !a)}>{chatAperta ? 'Nascondi' : 'Mostra'}</button>
                </div>
                {chatAperta && (
                  <div className="bop-card__body">
                    <div className="assist-thread">
                      {ticket.chat.messaggi.map(m => (
                        <div key={m.id} className={`assist-msg ${m.ruolo === 'ASSISTENTE' ? 'assist-msg--staff' : 'assist-msg--utente'}`}>
                          <div className="assist-msg__head">
                            <span className="assist-msg__autore">{m.ruolo === 'ASSISTENTE' ? 'Assistente' : ticket.createdByName}</span>
                            <span className="bop-text-muted">{new Date(m.createdAt).toLocaleString('it-IT')}</span>
                          </div>
                          <p className="assist-msg__testo">{m.testo}</p>
                          {m.allegati && m.allegati.length > 0 && (
                            <p className="bop-text-muted">📎 {m.allegati.length === 1 ? 'Uno screenshot' : `${m.allegati.length} screenshot`} incollato in chat (copiato negli allegati del ticket)</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {ticket.chat.documentiKb.length > 0 && (
                      <p className="bop-text-muted">Documenti consultati: {ticket.chat.documentiKb.join(', ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Thread risposte */}
            <div className="bop-card">
              <div className="bop-card__header"><h3>Conversazione ({ticket.risposte.length})</h3></div>
              <div className="bop-card__body">
                {ticket.risposte.length === 0 ? (
                  <div className="bop-empty-state">Nessuna risposta. Scrivi il primo messaggio.</div>
                ) : (
                  <div className="assist-thread">
                    {ticket.risposte.map(r => (
                      <div key={r.id} className={`assist-msg ${r.interna ? 'assist-msg--interna' : r.isStaff ? 'assist-msg--staff' : 'assist-msg--utente'}`}>
                        <div className="assist-msg__head">
                          <span className="assist-msg__autore">
                            {r.utenteName}
                            {r.isStaff && !r.interna && <span className="bop-badge bop-badge--primary assist-msg__staff">Staff</span>}
                            {r.interna && <span className="bop-badge bop-badge--warning assist-msg__staff" title="Visibile solo allo staff, non a chi ha aperto il ticket">Nota interna</span>}
                          </span>
                          <span className="bop-text-muted">{new Date(r.createdAt).toLocaleString('it-IT')}</span>
                        </div>
                        <p className="assist-msg__testo">{r.testo}</p>
                      </div>
                    ))}
                  </div>
                )}

                {canEdit && ticket.stato !== 'CHIUSO' && (
                  <div className="assist-reply">
                    <textarea
                      className="bop-input"
                      rows={3}
                      placeholder="Scrivi una risposta…"
                      value={nuovaRisposta}
                      onChange={e => setNuovaRisposta(e.target.value)}
                    />
                    <button className="bop-btn bop-btn-primary" onClick={inviaRisposta} disabled={inviando || !nuovaRisposta.trim()}>
                      {inviando ? 'Invio…' : 'Invia risposta'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Colonna laterale — gestione staff */}
          {gestisceAssistenza && (
            <div className="assist-scheda-side">
              <div className="bop-card">
                <div className="bop-card__header"><h3>Gestione</h3></div>
                <div className="bop-card__body">
                  <div className="bop-form-group">
                    <label className="bop-label">Stato</label>
                    <select
                      className="bop-select"
                      value={ticket.stato}
                      disabled={savingStato}
                      onChange={e => aggiornaCampo({ stato: e.target.value as TicketStato })}
                    >
                      {(Object.keys(TICKET_STATO_LABEL) as TicketStato[]).map(s => (
                        <option key={s} value={s}>{TICKET_STATO_LABEL[s]}</option>
                      ))}
                    </select>
                    {ticket.stato === 'RISOLTO' && (
                      <p className="bop-text-muted">
                        In attesa che {ticket.createdByName} confermi la chiusura
                        {ticket.remindConfermaCount ? ` · ${ticket.remindConfermaCount} ${ticket.remindConfermaCount === 1 ? 'promemoria inviato' : 'promemoria inviati'}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Priorità</label>
                    <select
                      className="bop-select"
                      value={ticket.priorita}
                      disabled={savingStato}
                      onChange={e => aggiornaCampo({ priorita: e.target.value as TicketPriorita })}
                    >
                      {(Object.keys(TICKET_PRIORITA_LABEL) as TicketPriorita[]).map(p => (
                        <option key={p} value={p}>{TICKET_PRIORITA_LABEL[p]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <button className="bop-btn bop-btn-danger" onClick={eliminaTicket} disabled={eliminando || savingStato}>
                      {eliminando ? 'Eliminazione…' : 'Elimina ticket'}
                    </button>
                    <div className="bop-text-muted assist-elimina-nota">Cancella ticket, allegati, risposte e log. Non reversibile.</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
